// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { FileCheck2, Loader2, AlertCircle, Plus, Upload, Download, Archive, FileText, Sparkles } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'

const MUTE = 'var(--color-text-mute, #6b7280)'
const BORDER = 'var(--color-border, #e5e7eb)'
const TYPES = ['baa', 'report', 'contract', 'sow', 'policy', 'attestation'] as const

// Program controls → fillable Word templates (served as static files from /compliance-templates/).
// control_id matches the compliance-record control_id, so an uploaded filled copy shows as "on file".
const TEMPLATES: { control_id: string; name: string; cite: string; file: string }[] = [
  { control_id: 'P-1', name: 'HIPAA Security Risk Analysis', cite: '164.308(a)(1)', file: 'hipaa-risk-analysis.docx' },
  { control_id: 'P-2', name: 'Risk Management Plan', cite: '164.308(a)(1)', file: 'risk-management-plan.docx' },
  { control_id: 'P-3', name: 'Security Officer Designation', cite: '164.308(a)(2)', file: 'security-officer-designation.docx' },
  { control_id: 'P-4', name: 'Workforce Security & Access Management', cite: '164.308(a)(3-4)', file: 'workforce-access-policy.docx' },
  { control_id: 'P-5', name: 'Security Awareness & Training', cite: '164.308(a)(5)', file: 'security-training-policy.docx' },
  { control_id: 'P-6', name: 'Security Incident Response Plan', cite: '164.308(a)(6)', file: 'incident-response-plan.docx' },
  { control_id: 'P-7', name: 'Contingency Plan (Backup / DR)', cite: '164.308(a)(7)', file: 'contingency-dr-plan.docx' },
  { control_id: 'P-8', name: 'Periodic Security Evaluation', cite: '164.308(a)(8)', file: 'periodic-evaluation.docx' },
  { control_id: 'P-9', name: 'Physical & Facility Security', cite: '164.310', file: 'physical-safeguards-policy.docx' },
  { control_id: 'P-10', name: 'Device & Media Controls', cite: '164.310(d)', file: 'device-media-controls.docx' },
  { control_id: 'P-11', name: 'Policies & Documentation Retention', cite: '164.316', file: 'policies-retention-index.docx' },
  { control_id: 'BAA-1', name: 'Business Associate Agreement (template)', cite: '164.314(a)', file: 'business-associate-agreement-template.docx' },
]

function statusColor(s: string): { fg: string; bg: string } {
  switch (s) {
    case 'active': return { fg: '#15803d', bg: '#dcfce7' }
    case 'expiring': return { fg: '#b45309', bg: '#fef3c7' }
    case 'expired': return { fg: '#b91c1c', bg: '#fee2e2' }
    default: return { fg: '#6b7280', bg: '#f3f4f6' } // pending
  }
}

export default function ComplianceTab() {
  const [records, setRecords] = useState<api.ComplianceRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  useEffect(() => { void load() }, [])
  async function load() {
    setLoading(true); setError(null)
    try { setRecords((await api.listComplianceRecords()).records) }
    catch (e: any) { setError(e?.message || 'Failed to load compliance records') }
    finally { setLoading(false) }
  }

  return (
    <div style={{ maxWidth: '100%' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, margin: '0 0 4px' }}>
            <FileCheck2 size={18} /> Compliance & BAAs
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: MUTE }}>
            Track business associate agreements, audit reports, contracts and SOWs — with effective and
            expiration dates. Expiring and expired items surface as open security items automatically.
          </p>
        </div>
        <button onClick={() => setAdding(a => !a)} style={primaryBtn}><Plus size={14} /> Add record</button>
      </header>

      {error && <div style={errBox}><AlertCircle size={14} style={{ verticalAlign: '-2px', marginRight: 6 }} />{error}</div>}

      {adding && <AddForm onDone={() => { setAdding(false); void load() }} onError={setError} />}

      <RequiredDocs records={records} />

      {loading ? (
        <div style={{ padding: 32, textAlign: 'center', color: MUTE }}><Loader2 size={18} className="spin" /> Loading…</div>
      ) : records.length === 0 ? (
        <p style={{ padding: 16, fontSize: 13, color: MUTE }}>No compliance records yet. Add a BAA or report to start tracking.</p>
      ) : (
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
          {records.map((rec, i) => (
            <Row key={rec.id} rec={rec} first={i === 0} onChanged={load} onError={setError} />
          ))}
        </div>
      )}

      <ReportsSection onError={setError} />
    </div>
  )
}

function Row({ rec, first, onChanged, onError }: {
  rec: api.ComplianceRecord; first: boolean; onChanged: () => void; onError: (m: string) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const c = statusColor(rec.status)

  async function upload(f: File) {
    setBusy(true)
    try { await api.uploadComplianceDocument(rec.id, f); onChanged() }
    catch (e: any) { onError(e?.message || 'Upload failed') }
    finally { setBusy(false) }
  }
  async function archive() {
    if (!confirm(`Archive "${rec.title}"?`)) return
    setBusy(true)
    try { await api.archiveComplianceRecord(rec.id); onChanged() }
    catch (e: any) { onError(e?.message || 'Archive failed') }
    finally { setBusy(false) }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderTop: first ? undefined : `1px solid ${BORDER}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {rec.title} <span style={{ fontSize: 11, color: MUTE, fontWeight: 400 }}>· {rec.record_type} · {rec.control_id}</span>
        </div>
        <div style={{ fontSize: 12, color: MUTE }}>
          {rec.counterparty ? `${rec.counterparty} · ` : ''}
          {rec.effective_date ? `eff ${rec.effective_date}` : 'no effective date'}
          {rec.expiration_date ? ` · exp ${rec.expiration_date}` : ''}
          {rec.original_filename ? ` · ${rec.original_filename}` : ''}
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: c.fg, background: c.bg, padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase' }}>{rec.status}</span>
      {rec.has_document
        ? <a href={api.complianceDocumentUrl(rec.id)} style={iconBtn} title="Download"><Download size={14} /></a>
        : <button onClick={() => fileRef.current?.click()} disabled={busy} style={iconBtn} title="Upload document"><Upload size={14} /></button>}
      <button onClick={archive} disabled={busy} style={iconBtn} title="Archive"><Archive size={14} /></button>
      <input ref={fileRef} type="file" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) void upload(f) }} />
    </div>
  )
}

function AddForm({ onDone, onError }: { onDone: () => void; onError: (m: string) => void }) {
  const [f, setF] = useState({ record_type: 'baa', control_id: '', title: '', counterparty: '', effective_date: '', expiration_date: '', notes: '' })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setF(p => ({ ...p, [k]: v }))

  async function save() {
    if (!f.control_id.trim() || !f.title.trim()) { onError('Control ID and title are required'); return }
    setSaving(true)
    try {
      await api.createComplianceRecord({
        control_id: f.control_id.trim(), record_type: f.record_type, title: f.title.trim(),
        counterparty: f.counterparty || undefined, effective_date: f.effective_date || undefined,
        expiration_date: f.expiration_date || undefined, notes: f.notes || undefined,
      })
      onDone()
    } catch (e: any) { onError(e?.message || 'Save failed') }
    finally { setSaving(false) }
  }

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: 14, marginBottom: 16, background: 'var(--color-surface, #fafafa)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <label style={lbl}>Type
          <SearchableSelect value={f.record_type} onChange={v => set('record_type', v)} ariaLabel="Record type"
            options={TYPES.map(t => ({ value: t, label: t }))} style={{ minWidth: 130 }} />
        </label>
        <label style={lbl}>Control ID
          <input value={f.control_id} onChange={e => set('control_id', e.target.value)} placeholder="e.g. BAA-5" style={inp} />
        </label>
        <label style={{ ...lbl, flex: '1 1 220px' }}>Title
          <input value={f.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Anthropic BAA" style={inp} />
        </label>
        <label style={lbl}>Counterparty
          <input value={f.counterparty} onChange={e => set('counterparty', e.target.value)} style={inp} />
        </label>
        <label style={lbl}>Effective
          <input type="date" value={f.effective_date} onChange={e => set('effective_date', e.target.value)} style={inp} />
        </label>
        <label style={lbl}>Expiration
          <input type="date" value={f.expiration_date} onChange={e => set('expiration_date', e.target.value)} style={inp} />
        </label>
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <button onClick={save} disabled={saving} style={primaryBtn}>{saving ? 'Saving…' : 'Save record'}</button>
        <span style={{ fontSize: 12, color: MUTE, alignSelf: 'center' }}>Upload the document after saving.</span>
      </div>
    </div>
  )
}

function ReportsSection({ onError }: { onError: (m: string) => void }) {
  const [state, setState] = useState('CA')
  const [frameworks, setFrameworks] = useState<api.ComplianceFramework[]>([])
  const [framework, setFramework] = useState('')
  const [reports, setReports] = useState<api.ComplianceReport[]>([])
  const [gen, setGen] = useState(false)

  useEffect(() => { void loadFrameworks(state) }, [state])
  useEffect(() => { void loadReports() }, [])
  async function loadFrameworks(s: string) {
    try {
      const r = await api.listComplianceFrameworks(s)
      setFrameworks(r.frameworks); setFramework(r.frameworks[0]?.code || '')
    } catch (e: any) { onError(e?.message || 'Failed to load frameworks') }
  }
  async function loadReports() {
    try { setReports((await api.listComplianceReports()).reports) } catch { /* soft */ }
  }
  async function generate() {
    if (!framework) return
    setGen(true)
    try { await api.generateComplianceReport(framework, state); await loadReports() }
    catch (e: any) { onError(e?.message || 'Report generation failed — the reporting key may not be configured yet') }
    finally { setGen(false) }
  }

  return (
    <section style={{ marginTop: 28 }}>
      <h3 style={{ fontSize: 15, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <FileText size={16} /> Audit readiness reports
      </h3>
      <p style={{ fontSize: 12, color: MUTE, margin: '0 0 10px' }}>
        Generate the deliverable set — control matrix, gap analysis, readiness summary — for a framework.
        Drafts for your review, not an audit opinion.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end', marginBottom: 12 }}>
        <label style={lbl}>State
          <SearchableSelect value={state} onChange={setState} ariaLabel="State"
            options={[{ value: 'CA', label: 'California' }, { value: 'US', label: 'Federal only' }]} style={{ minWidth: 150 }} />
        </label>
        <label style={{ ...lbl, flex: '1 1 260px' }}>Framework
          <SearchableSelect value={framework} onChange={setFramework} ariaLabel="Framework"
            options={frameworks.map(f => ({ value: f.code, label: f.name }))} style={{ minWidth: 240 }} />
        </label>
        <button onClick={generate} disabled={gen || !framework} style={primaryBtn}>
          <Sparkles size={14} /> {gen ? 'Generating…' : 'Generate report'}
        </button>
      </div>
      {reports.length > 0 && (
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
          {reports.map((rep, i) => (
            <div key={rep.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderTop: i ? `1px solid ${BORDER}` : undefined }}>
              <span style={{ flex: 1, fontSize: 13 }}>
                {rep.framework.toUpperCase()}
                <span style={{ color: MUTE, fontSize: 12 }}>
                  {' · '}{rep.jurisdiction}{' · '}{new Date(rep.generated_at).toLocaleDateString()}
                  {rep.gap_count != null ? ` · ${rep.gap_count} gaps` : ''}
                </span>
              </span>
              <span style={{ fontSize: 11, color: MUTE, textTransform: 'uppercase' }}>{rep.status}</span>
              {rep.has_document && <a href={api.complianceReportUrl(rep.id)} style={iconBtn} title="Download"><Download size={14} /></a>}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function RequiredDocs({ records }: { records: api.ComplianceRecord[] }) {
  const onFile = (controlId: string) => records.some(r => r.control_id === controlId && r.has_document)
  return (
    <section style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 15, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <FileText size={16} /> Required documents
      </h3>
      <p style={{ fontSize: 12, color: MUTE, margin: '0 0 10px' }}>
        Download each template, complete it with your team, then upload the signed copy back to the vault below.
        These are starting points — review with your Security Officer and counsel before adopting.
      </p>
      <div style={{ border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
        {TEMPLATES.map((tpl, i) => {
          const done = onFile(tpl.control_id)
          return (
            <div key={tpl.control_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderTop: i ? `1px solid ${BORDER}` : undefined }}>
              <span style={{ fontSize: 12, color: MUTE, width: 44 }}>{tpl.control_id}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{tpl.name}</div>
                <div style={{ fontSize: 12, color: MUTE }}>§{tpl.cite}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: done ? '#15803d' : MUTE, background: done ? '#dcfce7' : '#f3f4f6', padding: '2px 8px', borderRadius: 999 }}>
                {done ? 'on file' : 'not started'}
              </span>
              <a href={`/compliance-templates/${tpl.file}`} download style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-text, #111827)', textDecoration: 'none', border: `1px solid ${BORDER}`, borderRadius: 6, padding: '5px 10px', fontWeight: 500 }}>
                <Download size={14} /> Template
              </a>
            </div>
          )
        })}
      </div>
    </section>
  )
}

const primaryBtn: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#111827', color: '#fff', border: 'none', padding: '7px 12px', fontSize: 13, borderRadius: 6, cursor: 'pointer', fontWeight: 500 }
const iconBtn: CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, border: `1px solid ${BORDER}`, borderRadius: 6, background: 'transparent', color: 'var(--color-text, #111827)', cursor: 'pointer', textDecoration: 'none' }
const lbl: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: MUTE }
const inp: CSSProperties = { border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 8px', fontSize: 13, minWidth: 120 }
const errBox: CSSProperties = { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }
